import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Loader2 } from 'lucide-react';
import { Button, Input } from '@ai-tutor/ui';
import type { StagedSource } from '../types';
import type { TabFocusHandle } from './UploadFileTab';

interface AddLinkTabProps {
  onAdd: (source: StagedSource) => void | Promise<void>;
}

const HELPER_DEFAULT =
  'Articles and YouTube videos. YouTube must have captions; pages behind a paywall, login, or heavy JavaScript may fail to extract.';
const HELPER_YOUTUBE =
  'YouTube - only videos with captions (manual or auto-generated) extract successfully.';

const YT_HOST_RE = /(?:^|\.)(?:youtube\.com|youtu\.be)$/i;

function parseUrl(raw: string): URL | null {
  try {
    const u = new URL(raw.trim());
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u;
  } catch {
    return null;
  }
}

export const AddLinkTab = forwardRef<TabFocusHandle, AddLinkTabProps>(
  function AddLinkTab({ onAdd }, ref) {
    const urlRef = useRef<HTMLInputElement>(null);
    const userEditedName = useRef(false);
    const [url, setUrl] = useState('');
    const [name, setName] = useState('');
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [isBusy, setIsBusy] = useState(false);

    useImperativeHandle(ref, () => ({
      focus: () => urlRef.current?.focus(),
    }));

    const parsed = useMemo(() => parseUrl(url), [url]);
    const isYouTube = parsed && YT_HOST_RE.test(parsed.hostname);
    const isValid = !!parsed;

    // Auto-fill display name from the URL host whenever the URL becomes
    // valid, unless the user has already typed something into the name
    // field. The userEditedName ref keeps user edits sticky.
    useEffect(() => {
      if (parsed && !userEditedName.current) {
        setName(parsed.hostname.replace(/^www\./, ''));
      }
    }, [parsed]);

    const handleNameChange = (val: string) => {
      userEditedName.current = true;
      setName(val);
    };

    const handleSubmit = async () => {
      if (!parsed) return;
      setSubmitError(null);
      setIsBusy(true);
      try {
        await onAdd({
          kind: 'link',
          url: parsed.toString(),
          name: name.trim() || undefined,
        });
        setUrl('');
        setName('');
        userEditedName.current = false;
      } catch (err: any) {
        setSubmitError(err?.message || 'Failed to add link');
      } finally {
        setIsBusy(false);
      }
    };

    return (
      // Sticky-footer layout matches PasteTextTab so the Add button is always
      // visible regardless of vertical content height.
      <div className='flex h-full flex-col'>
        <div className='flex flex-1 flex-col gap-4 overflow-y-auto px-4 pt-4 sm:px-6 sm:pt-6'>
          <p className='text-xs text-muted-foreground'>
            {isYouTube ? HELPER_YOUTUBE : HELPER_DEFAULT}
          </p>

          <div>
            <label
              htmlFor='attach-link-url'
              className='mb-1 block text-xs font-medium text-muted-foreground'
            >
              URL
            </label>
            {/* `h-11 sm:h-9` on both fields: 44px on a phone, like every auth
                field, the shared Input's 36px from `sm` up. */}
            <Input
              id='attach-link-url'
              ref={urlRef}
              placeholder='https://example.com/article'
              value={url}
              onChange={e => setUrl(e.target.value)}
              disabled={isBusy}
              className='h-11 border-border bg-card shadow-none outline-none ring-0 focus:border-brand-400 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:focus:border-primary sm:h-9'
            />
            {url.trim() && !parsed && (
              <p className='mt-1 text-xs text-red-500'>
                Enter a valid http(s) URL.
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor='attach-link-name'
              className='mb-1 block text-xs font-medium text-muted-foreground'
            >
              Display name{' '}
              <span className='text-muted-foreground'>(optional)</span>
            </label>
            <Input
              id='attach-link-name'
              placeholder='Auto-filled from URL'
              value={name}
              onChange={e => handleNameChange(e.target.value)}
              disabled={isBusy}
              className='h-11 border-border bg-card shadow-none outline-none ring-0 focus:border-brand-400 focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 dark:focus:border-primary sm:h-9'
            />
          </div>

          {submitError && <p className='text-xs text-red-500'>{submitError}</p>}

          <div className='pt-1 sm:flex sm:justify-end'>
            <Button
              type='button'
              size='sm'
              onClick={handleSubmit}
              disabled={!isValid || isBusy}
              className='h-11 w-full min-w-[120px] px-8 sm:h-8 sm:w-auto'
            >
              {isBusy ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Adding…
                </>
              ) : (
                'Add link'
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }
);
